import { AbilityBuilder, createMongoAbility, type MongoAbility, type RawRuleOf } from '@casl/ability'
import type { Actions, Subjects, SubjectStrings, AppAbility } from './ability.types.js'
import type { CaslRulesFunction } from '@kernel/config/types.js'
import { logger } from '@kernel/logger/index.js'
import type { FieldDenyRule } from './field-access.js'

export type { FieldDenyRule } from './field-access.js'

let customCaslRules: CaslRulesFunction | undefined

/** Seed-defined permissions: Map<roleName, Map<subject, actions[]>> */
let seedPermissions: Map<string, Map<string, string[]>> | null = null

/**
 * Registers seed-defined permissions from onSeed hook.
 * Applied as step 4 in defineAbilityFor (after entity definitions and custom rules).
 */
export function setSeedPermissions(perms: Map<string, Map<string, string[]>> | null): void {
  seedPermissions = perms
}

/** Clears seed permissions (called on stop/restart). */
export function clearSeedPermissions(): void {
  seedPermissions = null
}

/**
 * Registers a function to define custom CASL rules.
 * Runs after loading permissions from entity definitions.
 */
export function setCustomCaslRules(fn: CaslRulesFunction | undefined): void {
  customCaslRules = fn
}

/**
 * Clears registered custom CASL rules.
 */
export function clearCustomCaslRules(): void {
  customCaslRules = undefined
}

/**
 * Minimal user type for ability building.
 * Accepts any object with id (and optionally email).
 */
export interface MinimalUser {
  id: string
  email?: string
}

/**
 * Internal user type with index signature for dynamic property access.
 * Used only internally for condition interpolation.
 */
interface IndexableUser extends MinimalUser {
  [key: string]: unknown
}

/**
 * Interpolates variables in permission conditions.
 * Supports: ${user.id}, ${user.email}, etc.
 */
function interpolateConditions(
  conditions: Record<string, unknown>,
  user: IndexableUser
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(conditions)) {
    if (typeof value === 'string' && value.startsWith('${user.')) {
      const prop = value.slice(7, -1)
      const resolved = user[prop]
      if (resolved === undefined) {
        logger.warn({ property: prop, condition: key }, 'CASL condition interpolation: user property is undefined, condition may not work as expected')
      }
      result[key] = resolved
    } else {
      result[key] = value
    }
  }

  return result
}

/**
 * Entity permission definition from casl.permissions
 */
interface EntityPermission {
  actions: string[]
  conditions?: Record<string, unknown>
  fields?: string[]
  inverted?: boolean
}

/**
 * Permission value can be a single permission or array of permissions.
 * Array allows different configs per action (e.g., read without fields, update with fields).
 */
type PermissionValue = EntityPermission | EntityPermission[]

// Entity definitions registry (set by engine on init)
let entityDefinitionsRegistry: Map<string, { subject: string; permissions: Record<string, PermissionValue> }> | null = null

/**
 * Registers entity definitions for ability building.
 * Called by engine during initialization.
 */
export function setEntityDefinitions(
  definitions: Map<string, { subject: string; permissions: Record<string, PermissionValue> }>
): void {
  entityDefinitionsRegistry = definitions
  cachedAnonymousAbility = null
}

/**
 * Returns the number of entity definitions registered, or -1 if not initialized.
 * Useful for tests to verify that the registry was populated at boot.
 */
export function getEntityDefinitionsCount(): number {
  return entityDefinitionsRegistry ? entityDefinitionsRegistry.size : -1
}

/**
 * Clears entity definitions registry.
 * Useful for tests.
 */
export function clearEntityDefinitions(): void {
  entityDefinitionsRegistry = null
  cachedAnonymousAbility = null
}

// ---------------------------------------------------------------------------
// Field-level deny registry (FieldAccessConfig enforcement)
// ---------------------------------------------------------------------------

/**
 * Field deny registry: Map<subject, FieldDenyRule[]>.
 * Derived from `FieldDefinition.access` during boot (see field-access.ts).
 *
 * Unlike entityDefinitionsRegistry, this applies to ALL users including ADMIN
 * when `allowedRoles` is empty — bypasses the superuser gate by design.
 */
let fieldDenyRegistry: Map<string, FieldDenyRule[]> | null = null

/** Registers field deny rules from FieldAccessConfig. Called by engine at boot. */
export function setFieldDenyRules(rules: Map<string, FieldDenyRule[]>): void {
  fieldDenyRegistry = rules
  cachedAnonymousAbility = null
}

/** Clears field deny registry. Useful for tests. */
export function clearFieldDenyRules(): void {
  fieldDenyRegistry = null
  cachedAnonymousAbility = null
}

/**
 * Applies field-level `cannot` rules for the given user roles.
 *
 * Must be called AFTER `can('manage', 'all')` in defineAbilityFor — CASL
 * evaluates rules "last wins", so the cannot takes precedence over the
 * broad manage:all grant.
 */
function applyFieldDenyRules(
  userRoles: string[],
  cannot: AbilityBuilder<MongoAbility<[Actions, Subjects]>>['cannot']
): void {
  if (!fieldDenyRegistry) return
  for (const [subject, rules] of fieldDenyRegistry) {
    for (const rule of rules) {
      // allowedRoles empty → universal deny absolute (no exemptions)
      // allowedRoles non-empty → roles in userRoles are exempt
      const isExempt = rule.allowedRoles.some(r => userRoles.includes(r))
      if (isExempt) continue
      cannot(rule.action as Actions, subject as SubjectStrings, [rule.field])
    }
  }
}

/**
 * Applies a set of CASL permission rules for a given subject.
 * Handles normalization (single/array), condition interpolation, fields, and inverted rules.
 */
function applyPermissionRules(
  permsValue: PermissionValue,
  subject: SubjectStrings,
  user: IndexableUser,
  can: AbilityBuilder<MongoAbility<[Actions, Subjects]>>['can'],
  cannot: AbilityBuilder<MongoAbility<[Actions, Subjects]>>['cannot']
): void {
  const permsList = Array.isArray(permsValue) ? permsValue : [permsValue]
  for (const perm of permsList) {
    const { actions, conditions: rawConditions, fields, inverted } = perm
    const conditions = rawConditions ? interpolateConditions(rawConditions, user) : undefined
    const builder = inverted ? cannot : can
    for (const action of actions) {
      if (fields?.length) {
        builder(action as Actions, subject, fields, conditions)
      } else {
        builder(action as Actions, subject, conditions)
      }
    }
  }
}

// Roles with implicit manage:all (superusers)
const SUPERUSER_ROLES = ['ADMIN']

/**
 * Builds CASL abilities from role names and entity definitions.
 * Permissions are static in code (entity.casl.permissions[roleName]).
 *
 * ADMIN role automatically receives 'manage:all' permission.
 *
 * @param user - Authenticated user (needs id for condition interpolation)
 * @param roleNames - User's role names (e.g., ['ADMIN', 'EDITOR'])
 */
export async function defineAbilityFor(user: MinimalUser, roleNames: string[]): Promise<AppAbility> {
  const { can, cannot, build } = new AbilityBuilder<MongoAbility<[Actions, Subjects]>>(createMongoAbility)

  // Cast to IndexableUser for condition interpolation (dynamic property access)
  const indexableUser = user as IndexableUser

  // 1. Check for superuser roles (ADMIN) - implicit manage:all
  const isSuperuser = roleNames.some(role => SUPERUSER_ROLES.includes(role))
  if (isSuperuser) {
    can('manage', 'all')
    // CRITICAL: field deny rules AFTER can('manage', 'all') so cannot takes precedence
    // (CASL "last rule wins"). Moving this before would let manage:all override the deny.
    applyFieldDenyRules(roleNames, cannot)
  }

  // 2. Apply permissions from entity definitions (for non-superuser roles)
  if (entityDefinitionsRegistry && !isSuperuser) {
    for (const [, entityDef] of entityDefinitionsRegistry) {
      const subject = entityDef.subject as SubjectStrings

      // Apply '*' wildcard permissions first (baseline for all — including anonymous)
      const wildcardPerms = entityDef.permissions['*']
      if (wildcardPerms) {
        applyPermissionRules(wildcardPerms, subject, indexableUser, can, cannot)
      }

      // Apply '@' authenticated wildcard (any authenticated user, NOT anonymous)
      const authPerms = entityDef.permissions['@']
      if (authPerms) {
        applyPermissionRules(authPerms, subject, indexableUser, can, cannot)
      }

      // Apply role-specific permissions (additive on top of wildcards)
      for (const roleName of roleNames) {
        const rolePerms = entityDef.permissions[roleName]
        if (rolePerms) {
          applyPermissionRules(rolePerms, subject, indexableUser, can, cannot)
        }
      }
    }
  }

  // 2b. Apply field-level deny rules for non-superusers (outside entity registry gate).
  if (!isSuperuser) {
    applyFieldDenyRules(roleNames, cannot)
  }

  // 3. Apply custom rules (if registered)
  if (customCaslRules) {
    await customCaslRules(user as unknown as Record<string, unknown>, { can, cannot })
  }

  // 4. Apply seed-defined permissions (from onSeed roles.add with permissions)
  if (seedPermissions && !isSuperuser) {
    for (const roleName of roleNames) {
      const rolePerms = seedPermissions.get(roleName)
      if (!rolePerms) continue
      for (const [subject, actions] of rolePerms) {
        for (const action of actions) {
          can(action as Actions, subject as SubjectStrings)
        }
      }
    }
  }

  return build()
}

/** Dummy user for anonymous condition interpolation (no real user data) */
const ANONYMOUS_USER: IndexableUser = { id: '' }

/** Cached anonymous ability (rebuilt when entity definitions change) */
let cachedAnonymousAbility: AppAbility | null = null

/**
 * Builds CASL abilities for anonymous (unauthenticated) users.
 * Only applies '*' wildcard permissions from entity definitions.
 * No user-specific conditions, no role-specific permissions, no custom rules.
 */
export function defineAnonymousAbility(): AppAbility {
  if (cachedAnonymousAbility) return cachedAnonymousAbility

  const { can, cannot, build } = new AbilityBuilder<MongoAbility<[Actions, Subjects]>>(createMongoAbility)

  if (entityDefinitionsRegistry) {
    for (const [, entityDef] of entityDefinitionsRegistry) {
      const subject = entityDef.subject as SubjectStrings
      const wildcardPerms = entityDef.permissions['*']
      if (wildcardPerms) {
        applyPermissionRules(wildcardPerms, subject, ANONYMOUS_USER, can, cannot)
      }
    }
  }

  // Anonymous users have no roles — only universal deny (allowedRoles=[]) applies.
  applyFieldDenyRules([], cannot)

  cachedAnonymousAbility = build()
  return cachedAnonymousAbility
}

/** Invalidates the cached anonymous ability (call when entity definitions change) */
export function clearAnonymousAbility(): void {
  cachedAnonymousAbility = null
}

// Serialize abilities for sending to frontend
export function packRules(ability: AppAbility): RawRuleOf<AppAbility>[] {
  return ability.rules as RawRuleOf<AppAbility>[]
}

// Rebuild abilities from serialized rules
export function unpackRules(rules: RawRuleOf<AppAbility>[]): AppAbility {
  return createMongoAbility<[Actions, Subjects]>(rules)
}
